import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ShieldCheck, ChevronRight } from "lucide-react";
import { useCart } from "../lib/cartContext";
import { formatSGD } from "../lib/mockData";
import { startCheckout } from "../lib/checkout";
import { toast } from "sonner";

const TIME_SLOTS = ["9AM – 12PM", "12PM – 3PM", "3PM – 6PM", "6PM – 9PM"];

export default function Checkout() {
  const { detailed, subtotal, discount, deliveryFee, total, appliedPromo, applyPromo, clearPromo } = useCart();
  const [busy, setBusy] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    recipientName: "",
    recipientMobile: "",
    address: "",
    postalCode: "",
    deliveryDate: "",
    timeSlot: TIME_SLOTS[0],
    giftMessage: "",
    orderNotes: "",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (detailed.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-4xl text-emerald">Your cart is empty</h1>
        <Link to="/shop" className="mt-6 inline-block bg-emerald text-cream px-6 py-3 text-sm">
          Browse Hampers
        </Link>
      </div>
    );
  }

  const onApplyPromo = () => {
    const code = promoInput.trim();
    if (!code) return;
    const res = applyPromo(code);
    if (res.ok) {
      toast.success(`Promo code ${code} applied`);
    } else if (res.reason === "min") {
      toast.error(
        `This code requires a minimum spend of ${formatSGD(res.minPurchase)}. Add ${formatSGD(res.shortfall)} more to use it.`
      );
    } else if (res.reason === "noEligible") {
      toast.error("This code doesn't apply to any items in your cart.");
    } else {
      toast.error("Invalid or expired promo code");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["fullName", "email", "mobile", "recipientName", "recipientMobile", "address", "postalCode", "deliveryDate"];
    if (required.some((k) => !form[k].trim())) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!/^\d{6}$/.test(form.postalCode.trim())) {
      toast.error("Singapore postal code must be 6 digits");
      return;
    }
    if (form.deliveryDate < new Date().toISOString().slice(0, 10)) {
      toast.error("Delivery date cannot be in the past");
      return;
    }

    setBusy(true);
    try {
      // The Cloud Function re-prices the cart, checks stock, validates the promo,
      // creates the order, and returns a Stripe Checkout URL.
      const { url } = await startCheckout({
        items: detailed.map((i) => ({ productId: i.productId, qty: i.qty })),
        customer: { fullName: form.fullName.trim(), email: form.email.trim(), mobile: form.mobile.trim() },
        recipient: {
          name: form.recipientName.trim(),
          mobile: form.recipientMobile.trim(),
          address: form.address.trim(),
          postalCode: form.postalCode.trim(),
        },
        delivery: {
          date: form.deliveryDate,
          timeSlot: form.timeSlot,
          giftMessage: form.giftMessage.trim(),
          orderNotes: form.orderNotes.trim(),
        },
        promoCode: appliedPromo?.code || null,
      });
      if (!url) throw new Error("No checkout URL returned");
      window.location.href = url; // redirect to Stripe's secure checkout
    } catch (err) {
      setBusy(false);
      const msg = err?.message || "Could not start checkout. Please try again.";
      // The server refused the promo (expired / already used by this customer /
      // limit reached). Drop it so the customer can simply retry at the real price.
      if (/promo code/i.test(msg)) {
        clearPromo();
        setPromoInput("");
      }
      toast.error(msg);
    }
  };

  const inputCls =
    "w-full px-4 py-2.5 bg-cream-2 border border-gold/25 rounded-lg text-sm outline-none focus:ring-2 focus:ring-gold focus:border-transparent";
  const labelCls = "block text-xs uppercase tracking-widest text-emerald-light/80 font-medium mb-1.5";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="checkout-page">
      <h1 className="font-serif text-4xl md:text-5xl text-emerald">
        <span className="italic text-gold">Checkout</span>
      </h1>
      <div className="text-sm text-emerald-light/70 mt-2">
        <Link to="/cart" className="hover:text-gold">Cart</Link>
        <ChevronRight size={12} className="inline mx-1" />
        <span className="text-emerald">Checkout</span>
      </div>

      <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
        <div className="space-y-8">
          {/* Customer */}
          <section className="bg-white border border-gold/25 rounded-xl p-6 md:p-8">
            <h2 className="font-serif text-2xl text-emerald">Customer Details</h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input value={form.fullName} onChange={(e) => setField("fullName", e.target.value)} className={inputCls} data-testid="input-fullName" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} data-testid="input-email" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Mobile Number *</label>
                <input value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} placeholder="+65" className={inputCls} data-testid="input-mobile" />
              </div>
            </div>
          </section>

          {/* Recipient */}
          <section className="bg-white border border-gold/25 rounded-xl p-6 md:p-8">
            <h2 className="font-serif text-2xl text-emerald">Recipient Details</h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Recipient Name *</label>
                <input value={form.recipientName} onChange={(e) => setField("recipientName", e.target.value)} className={inputCls} data-testid="input-recipientName" />
              </div>
              <div>
                <label className={labelCls}>Recipient Mobile *</label>
                <input value={form.recipientMobile} onChange={(e) => setField("recipientMobile", e.target.value)} className={inputCls} data-testid="input-recipientMobile" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Delivery Address *</label>
                <input value={form.address} onChange={(e) => setField("address", e.target.value)} className={inputCls} data-testid="input-address" />
              </div>
              <div>
                <label className={labelCls}>Postal Code *</label>
                <input value={form.postalCode} onChange={(e) => setField("postalCode", e.target.value)} className={inputCls} data-testid="input-postalCode" />
              </div>
            </div>
          </section>

          {/* Delivery */}
          <section className="bg-white border border-gold/25 rounded-xl p-6 md:p-8">
            <h2 className="font-serif text-2xl text-emerald">Delivery Details</h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Preferred Delivery Date *</label>
                <input type="date" value={form.deliveryDate} onChange={(e) => setField("deliveryDate", e.target.value)} className={inputCls} data-testid="input-deliveryDate" />
              </div>
              <div>
                <label className={labelCls}>Time Slot</label>
                <select value={form.timeSlot} onChange={(e) => setField("timeSlot", e.target.value)} className={inputCls} data-testid="input-timeSlot">
                  {TIME_SLOTS.map((t) => (<option key={t}>{t}</option>))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Gift Message (optional)</label>
                <textarea rows={3} value={form.giftMessage} onChange={(e) => setField("giftMessage", e.target.value)} className={inputCls} data-testid="input-giftMessage" placeholder="Handwritten on a premium keepsake card" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Order Notes (optional)</label>
                <textarea rows={2} value={form.orderNotes} onChange={(e) => setField("orderNotes", e.target.value)} className={inputCls} data-testid="input-orderNotes" />
              </div>
            </div>
          </section>

          {/* Payment placeholder */}
          <section className="bg-emerald text-cream rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3">
              <CreditCard size={22} className="text-gold" />
              <h2 className="font-serif text-2xl">Payment</h2>
            </div>
            <p className="mt-3 text-sm text-cream/80">
              You'll be redirected to <span className="text-gold font-semibold">Stripe</span>'s secure checkout to pay via{" "}
              <span className="text-gold font-semibold">PayNow</span>, credit/debit card, Apple&nbsp;Pay or Google&nbsp;Pay.
            </p>
            <div className="mt-4 flex items-center gap-2 text-xs text-cream/70">
              <ShieldCheck size={14} className="text-gold" />
              256-bit SSL secured checkout
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-gold/25 rounded-xl p-6 h-fit sticky top-32">
          <h3 className="font-serif text-2xl text-emerald">Order Summary</h3>
          <div className="mt-4 space-y-3 max-h-64 overflow-y-auto pr-2">
            {detailed.map((i) => (
              <div key={i.productId} className="flex gap-3">
                <div className="w-14 h-14 rounded overflow-hidden bg-cream-2 shrink-0">
                  <img
                    src={i.product.images[0]}
                    alt=""
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-emerald truncate">{i.product.name}</div>
                  <div className="text-xs text-emerald-light/70">Qty: {i.qty}</div>
                </div>
                <div className="text-sm text-emerald font-medium">{formatSGD(i.lineTotal)}</div>
              </div>
            ))}
          </div>
          {/* Promo code (scope: apply promo during checkout) */}
          <div className="mt-5 border-t border-gold/20 pt-4">
            <label className={labelCls}>Promo Code</label>
            {appliedPromo ? (
              <div className="flex items-center justify-between bg-gold/10 border border-gold/30 rounded-lg px-3 py-2">
                <span className="text-sm font-semibold text-emerald">{appliedPromo.code}</span>
                <button
                  type="button"
                  onClick={() => {
                    clearPromo();
                    setPromoInput("");
                    toast.success("Promo removed");
                  }}
                  className="text-xs text-emerald-light/70 hover:text-emerald underline"
                  data-testid="checkout-remove-promo"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    // Never let Enter submit the checkout form from this field.
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onApplyPromo();
                    }
                  }}
                  placeholder="Enter code"
                  className={`${inputCls} flex-1`}
                  data-testid="checkout-promo-input"
                />
                <button
                  type="button"
                  onClick={onApplyPromo}
                  className="px-4 py-2.5 text-sm bg-emerald text-cream rounded-lg hover:bg-emerald-dark transition-colors"
                  data-testid="checkout-apply-promo"
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          <div className="mt-5 border-t border-gold/20 pt-4 space-y-2.5 text-sm">
            <div className="flex justify-between text-emerald-light/80"><span>Subtotal</span><span>{formatSGD(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-gold"><span>Discount</span><span>− {formatSGD(discount)}</span></div>
            )}
            <div className="flex justify-between text-emerald-light/80"><span>Delivery</span><span>{deliveryFee === 0 ? "Free" : formatSGD(deliveryFee)}</span></div>
            <div className="flex justify-between font-serif text-2xl text-emerald border-t border-gold/20 pt-3">
              <span>Total</span><span>{formatSGD(total)}</span>
            </div>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-emerald px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="proceed-to-payment-btn"
          >
            {busy ? "Redirecting to secure payment…" : (<>Proceed to Payment <ChevronRight size={16} /></>)}
          </button>
          <p className="mt-3 text-[11px] text-emerald-light/70 text-center">
            By placing your order, you agree to our Terms & Conditions.
          </p>
        </aside>
      </form>
    </div>
  );
}
